import CircleIcon from "@mui/icons-material/Circle";
import { DateTime } from "luxon";
import { RRule } from "rrule";

const LIST_VIEWS = ["listMonth", "listWeek", "listDay"];
const EVENT_HORIZONTAL_PADDING = 4;
const EVENT_VERTICAL_PADDING = 2;

const eventConfirmationStatus = {
  "not-confirmed": "#cc0000",
  tbc: "#cc0000",
  pending: "#ffa70f",
  confirmed: "#32CD32",
};

const wrapEvent = (children, overrides = {}) => (
  <div
    style={{
      padding: `${EVENT_VERTICAL_PADDING}px ${EVENT_HORIZONTAL_PADDING}px`,
      boxSizing: "border-box",
      width: "100%",
      height: "100%",
      ...overrides,
    }}
  >
    {children}
  </div>
);

const flexRow = (overrides = {}) => ({
  display: "flex",
  alignItems: "center",
  height: "100%",
  ...overrides,
});

const flexColumn = (overrides = {}) => ({
  display: "flex",
  flexDirection: "column",
  ...overrides,
});

const textBase = (overrides = {}) => ({
  fontWeight: 600,
  fontSize: 11,
  ...overrides,
});

const accentText = (overrides = {}) => ({
  fontWeight: 800,
  fontSize: 12,
  ...overrides,
});

const uppercaseStrong = (overrides = {}) => ({
  fontWeight: 800,
  fontSize: 11.5,
  textTransform: "uppercase",
  ...overrides,
});

const circleStyle = (status) => ({
  marginRight: 4,
  fill: status
    ? eventConfirmationStatus[status] || eventConfirmationStatus.tbc
    : eventConfirmationStatus.tbc,
});

const listContainer = (backgroundColor, textColor) => ({
  backgroundColor,
  color: textColor,
  padding: "12px 16px",
  borderRadius: "6px",
  margin: "2px 0",
  border: `1px solid ${backgroundColor}`,
  minHeight: "60px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  cursor: "pointer",
  transition: "all 0.2s ease",
});

const listTitle = {
  fontWeight: 700,
  fontSize: 14,
  marginBottom: 4,
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const listStudioPill = (theme) => ({
  fontSize: 12,
  fontWeight: 500,
  opacity: 0.8,
  backgroundColor:
    theme === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
  padding: "2px 6px",
  borderRadius: "3px",
});

const listMetaRow = {
  fontSize: 12,
  opacity: 0.9,
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
};

const listNotes = (overrides = {}) => ({
  fontSize: 12,
  opacity: 0.8,
  marginTop: 4,
  fontStyle: "italic",
  ...overrides,
});

const listStaffTag = {
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const staffStatusStyle = (isAssigned, isEmployed) => ({
  display: "inline",
  color: isAssigned ? "#f44336" : isEmployed ? "" : "#d92b9f",
  fontWeight: isAssigned ? "800" : isEmployed ? "" : "700",
  backgroundColor: isAssigned
    ? "rgba(255, 87, 34, 0.25)"
    : isEmployed
    ? ""
    : "rgba(217, 43, 159, 0.2)",
  border: isAssigned
    ? "1px solid rgba(211, 47, 47, 0.4)"
    : isEmployed
    ? ""
    : "1px solid #d92b9f",
  padding: isAssigned || !isEmployed ? "0px 4px 1px 4px" : "",
  borderRadius: isAssigned || !isEmployed ? "2px" : "",
  textDecoration: !isEmployed && !isAssigned ? "underline" : "",
});

const buildPrefixLabel = (extendedProps = {}) => {
  const { eventPrefix, eventPrefixCustom } = extendedProps;
  if (eventPrefix === "AM" || eventPrefix === "PM") {
    return `${eventPrefix} - `;
  }
  if (eventPrefix === "Custom" && eventPrefixCustom) {
    return `${eventPrefixCustom} - `;
  }
  return "";
};

const parseStudioSmall = (studio) => {
  if (studio === "Studio 1") {
    return "Suite 401";
  } else if (studio === "Studio 2A") {
    return "Suite 403 W";
  } else if (studio === "Studio 2B") {
    return "Suite 403 M";
  } else if (studio === "Studio 2C") {
    return "Suite 403 D";
  } else if (studio === "Studio 3") {
    return "Suite 302";
  } else if (studio === "Studio 4") {
    return "Suite 301";
  }
  return "";
};

const parseShooter = (data) => {
  if (data.jobTypeFull.toLowerCase().includes("video")) {
    return "(V) ";
  }
  return "(P) ";
};

const parseOperator = (data) => {
  if (data.jobTypeFull.toLowerCase().includes("video")) {
    return "(VO) ";
  }
  return "(O) ";
};

const capitalizeFirstLetter = (text = "") => {
  if (!text) {
    return "";
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const checkIfStaffEmployed = (staff, staffData = []) => {
  if (!staff) return true;
  const record = staffData.find((sd) => sd.id === staff);
  return record ? !!record.currentlyEmployed : true;
};

const parseFreq = (freq) => {
  if (freq === "weekly") return RRule.WEEKLY;
  return undefined;
};

const parseByWeekDay = (byweekday = []) => {
  const map = {
    mo: RRule.MO,
    tu: RRule.TU,
    we: RRule.WE,
    th: RRule.TH,
    fr: RRule.FR,
    sa: RRule.SA,
    su: RRule.SU,
  };
  return byweekday.map((day) => map[day]).filter(Boolean);
};

const checkIfStaffAssigned = (option, events, event, showDoubles) => {
  if (!option || !events || !event?.event || !showDoubles) return false;

  const staffId = typeof option === "string" ? option : option?.id;
  if (!staffId) return false;

  const currentEventId = event.event.extendedProps?.id || event.event.id;
  const currentEventStartDt = DateTime.fromJSDate(event.event.start).setZone(
    "Australia/Sydney"
  );
  const currentEventDate = currentEventStartDt.toFormat("yyyy-MM-dd");

  const recurringEvents = events
    .filter(
      (e) =>
        e?.extendedProps?.recurringEvent &&
        e?.extendedProps?.photographer === staffId
    )
    .map((e) => {
      const freq = parseFreq(e.rrule.freq);
      const byweekday = parseByWeekDay(e.rrule.byweekday);
      const rule = new RRule({
        freq,
        interval: e.rrule.interval,
        byweekday,
        dtstart: new Date(e.rrule.dtstart),
        until: new Date(e.rrule.until),
      });
      return rule.all();
    })
    .flat()
    .filter((d) => {
      const repeatDateDt = DateTime.fromJSDate(d).setZone("Australia/Sydney");
      const repeatDate = repeatDateDt.toFormat("yyyy-MM-dd");
      return repeatDate === currentEventDate;
    });

  if (recurringEvents.length > 1) {
    return true;
  }

  const roles = [
    { key: "photographer" },
    { key: "operator" },
    { key: "stylist" },
    { key: "additionalPhotographers", isArray: true },
    { key: "additionalOperators", isArray: true },
    { key: "additionalStylists", isArray: true },
  ];

  const assignments = [];

  events.forEach((e) => {
    if (!e.start) return;

    const eventId = e?.extendedProps?.id || e.id;
    if (eventId === currentEventId) return;

    let eventStartDt;
    if (typeof e.start === "string") {
      eventStartDt = DateTime.fromISO(e.start).setZone("Australia/Sydney");
    } else if (e.start instanceof Date && !isNaN(e.start.getTime())) {
      eventStartDt = DateTime.fromJSDate(e.start).setZone("Australia/Sydney");
    } else {
      console.warn("Event has invalid start date:", {
        id: eventId,
        title: e.title,
        start: e.start,
      });
      return;
    }

    let eventEndDt;
    if (!e.end) {
      console.warn("Event missing end date:", {
        id: eventId,
        title: e.title,
        start: e.start,
        end: e.end,
        allDay: e.allDay,
      });
      return;
    } else if (typeof e.end === "string") {
      eventEndDt = DateTime.fromISO(e.end).setZone("Australia/Sydney");
    } else if (e.end instanceof Date && !isNaN(e.end.getTime())) {
      eventEndDt = DateTime.fromJSDate(e.end).setZone("Australia/Sydney");
    } else {
      console.warn("Event has invalid end date:", {
        id: eventId,
        title: e.title,
        end: e.end,
      });
      return;
    }

    const eventDate = eventStartDt.toFormat("yyyy-MM-dd");
    if (eventDate !== currentEventDate) return;

    roles.forEach((role) => {
      const extendedProps = e?.extendedProps;
      if (role.isArray) {
        const arrayField = extendedProps?.[role.key] || [];
        if (arrayField.includes(staffId)) {
          assignments.push({
            event: e,
            role: role.key,
            studio: extendedProps?.studio || "unknown",
            start: eventStartDt,
            end: eventEndDt,
            allDay: e.allDay,
          });
        }
      } else if (extendedProps?.[role.key] === staffId) {
        assignments.push({
          event: e,
          role: role.key,
          studio: extendedProps?.studio || "unknown",
          start: eventStartDt,
          end: eventEndDt,
          allDay: e.allDay,
        });
      }
    });
  });
  if (!assignments.length) {
    return false;
  }

  const currentEventStudio = event.event.extendedProps?.studio;
  if (!currentEventStudio) {
    return true;
  }

  const hasDifferentStudioAssignment = assignments.some(
    (assignment) => assignment.studio !== currentEventStudio
  );

  return hasDifferentStudioAssignment;
};

const findStaff = (staff, staffData = [], event, events, showDoubles) => {
  if (!staff) return "";

  for (let i = 0; i < staffData.length; i++) {
    if (staffData[i].id === staff) {
      const isAssigned = checkIfStaffAssigned(
        staffData[i],
        events,
        event,
        showDoubles
      );
      const isEmployed = checkIfStaffEmployed(staff, staffData);
      const name = staffData[i].preferredName || staffData[i].firstName;
      if (!name) break;
      // return <span>{name}</span>;
      return (
        <span style={staffStatusStyle(isAssigned, isEmployed)}>{name}</span>
      );
    }
  }

  if (staff && typeof staff === "object" && staff.type === "external") {
    return (
      <span
        style={{
          display: "inline",
          fontWeight: "bold",
          textDecoration: "underline",
          fontStyle: "italic",
        }}
      >
        {staff.name}
      </span>
    );
  }

  return staff;
};

const parseAdditionalStaff = (kind, event, staffData, showDoubles, events) => {
  const additionalStaff =
    event.event.extendedProps[`additional${capitalizeFirstLetter(kind)}s`];
  if (!additionalStaff) return null;

  return additionalStaff.map((s, index) => {
    const isAssigned = checkIfStaffAssigned(s, events, event, showDoubles);
    const isEmployed = checkIfStaffEmployed(s, staffData);
    return (
      <span key={`${s}-${index}`} style={{ marginLeft: 2 }}>
        &nbsp;/&nbsp;
        <span>{findStaff(s, staffData, event, events, showDoubles)}</span>
      </span>
    );
  });
};

const renderStaffStudioTitle = (
  title,
  photographerId,
  staffData,
  event,
  events,
  showDoubles
) => {
  if (!photographerId || !staffData) return title;

  const staffMember = staffData.find((s) => s.id === photographerId);
  if (!staffMember) return title;

  const namesToCheck = [
    staffMember.preferredName,
    staffMember.firstName,
  ].filter(Boolean);
  if (!namesToCheck.length) return title;

  const lowerTitle = title.toLowerCase();
  let nameToCheck = null;
  let nameIndex = -1;

  namesToCheck.forEach((name) => {
    const lowerName = name.toLowerCase();
    const foundIndex = lowerTitle.indexOf(lowerName);
    if (foundIndex !== -1 && (nameIndex === -1 || foundIndex < nameIndex)) {
      nameToCheck = name;
      nameIndex = foundIndex;
    }
  });

  if (nameIndex !== -1 && nameToCheck) {
    const beforeName = title.substring(0, nameIndex);
    const actualName = title.substring(
      nameIndex,
      nameIndex + nameToCheck.length
    );
    const afterName = title.substring(nameIndex + nameToCheck.length);

    const isAssigned = checkIfStaffAssigned(
      staffMember,
      events,
      event,
      showDoubles
    );
    const isEmployed = checkIfStaffEmployed(photographerId, staffData);

    return (
      <span>
        {beforeName}
        <span style={staffStatusStyle(isAssigned, isEmployed)}>
          {actualName}
        </span>
        {afterName}
      </span>
    );
  }

  return (
    <span>
      {findStaff(photographerId, staffData, event, events, showDoubles)}
      {parseAdditionalStaff(
        "photographer",
        event,
        staffData,
        showDoubles,
        events
      )}{" "}
      {photographerId ? "- " : ""}
      {title}
    </span>
  );
};

const eventContent = (event, staffData, events, showDoubles) => {
  const containerStyle = {
    display: "flex",
    flexDirection: event.view.type === "dayGridMonth" ? "row" : "column",
    width: "100%",
    height: "100%",
  };

  const notStaffAllDayStyle = flexRow({
    fontSize: 12,
    flexWrap: "wrap",
    height: "auto",
  });

  const staffAllDayStyle = {
    fontSize: 12,
    fontFamily:
      '"HelveticaNeue-Bold", "Helvetica Neue Bold", "Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif',
  };

  if (event.event.extendedProps.isBirthday) {
    const age = false;
    return wrapEvent(
      <div style={flexRow()}>
        <span style={textBase()}>
          {age
            ? `${event.event.title.replace("Birthday", `${age} Birthday`)}`
            : event.event.title}
        </span>
      </div>
    );
  }

  if (event.event.extendedProps.isPublicHoliday) {
    let emoji = "";
    if (event.event.title === "King's Birthday") {
      emoji = "🤴🏻";
    } else if (event.event.title === "Queen's Birthday") {
      emoji = "👸🏻";
    } else if (event.event.title === "Labour Day") {
      emoji = "⚒️";
    } else if (event.event.title === "Anzac Day") {
      emoji = "🦘";
    } else if (event.event.title === "Christmas Day") {
      emoji = "🎄";
    } else if (event.event.title === "Boxing Day") {
      emoji = "🥊";
    } else if (event.event.title === "New Year's Day") {
      emoji = "🎉";
    } else if (event.event.title === "Good Friday") {
      emoji = "🙏";
    } else if (event.event.title === "Easter Sunday") {
      emoji = "🐣";
    } else if (event.event.title === "Easter Monday") {
      emoji = "🐰";
    } else if (event.event.title === "Australia Day") {
      emoji = "🇦🇺";
    }
    return wrapEvent(
      <div style={flexRow()}>
        <span
          style={accentText({
            marginLeft: 0,
            color: event.event.textColor,
          })}
        >
          {`${emoji} `}
          {event.event.allDay
            ? event.event.title.toUpperCase()
            : `${event.event.title.toUpperCase()} - PUBLIC HOLIDAY`}
          {` ${emoji}`}
        </span>
      </div>
    );
  }

  if (event.event.allDay) {
    if (event.event.extendedProps.studio === "N/A") {
      return wrapEvent(
        <div style={flexRow()}>
          <span style={uppercaseStrong()}>
            {findStaff(
              event.event.extendedProps.photographer,
              staffData,
              event,
              events,
              showDoubles
            )}{" "}
            - N/A
          </span>
        </div>
      );
    }
    if (!event.event.extendedProps.staffAllDay) {
      return wrapEvent(
        <div style={flexRow()}>
          <span style={textBase()}>
            {buildPrefixLabel(event.event.extendedProps)}
            {event.event.extendedProps.studio === "Staff" ? (
              renderStaffStudioTitle(
                event.event.title,
                event.event.extendedProps.photographer,
                staffData,
                event,
                events,
                showDoubles
              )
            ) : event.event.extendedProps.studio === "Critical Events" ||
              event.event.extendedProps.studio === "Post Production" ? (
              <span>
                {findStaff(
                  event.event.extendedProps.photographer,
                  staffData,
                  event,
                  events,
                  showDoubles
                )}
                {parseAdditionalStaff(
                  "photographer",
                  event,
                  staffData,
                  showDoubles,
                  events
                )}{" "}
                {event.event.extendedProps.photographer ? "-" : ""}{" "}
                {event.event.title}
              </span>
            ) : (
              event.event.title
            )}
          </span>
        </div>
      );
    }
  }

  const timeTextRaw = event.timeText;
  const match = timeTextRaw.match(/(.*)( -)/);
  let timeText;
  if (match) {
    timeText = match[1];
  } else timeText = timeTextRaw;
  timeText = timeText.replace("am", " am");
  timeText = timeText.replace("pm", " pm");

  const match2 = event.timeText.match(/(- )(.*)/);
  let endtimeText = event.timeText;

  if (match2) {
    endtimeText = match2[2];
  }
  endtimeText = endtimeText.replace("am", " am");
  endtimeText = endtimeText.replace("pm", " pm");

  const returnExtendedPropsTitle = (
    <div style={containerStyle}>
      <div
        style={flexColumn({
          justifyContent: "space-between",
          height: "100%",
        })}
      >
        <div style={flexRow({ height: "auto" })}>
          <span style={textBase({ fontSize: 10, marginBottom: 6 })}>
            {timeText}
          </span>
          <span style={textBase({ marginLeft: 0 })}>
            {buildPrefixLabel(event.event.extendedProps)}
            {event.event.extendedProps.studio === "Staff" ? (
              renderStaffStudioTitle(
                event.event.extendedProps.title,
                event.event.extendedProps.photographer,
                staffData,
                event,
                events,
                showDoubles
              )
            ) : event.event.extendedProps.studio === "Critical Events" ||
              event.event.extendedProps.studio === "Post Production" ? (
              <span>
                {findStaff(
                  event.event.extendedProps.photographer,
                  staffData,
                  event,
                  events,
                  showDoubles
                )}
                {parseAdditionalStaff(
                  "photographer",
                  event,
                  staffData,
                  showDoubles,
                  events
                )}{" "}
                {event.event.extendedProps.photographer ? "-" : ""}{" "}
                {event.event.extendedProps.title}
              </span>
            ) : (
              event.event.extendedProps.title
            )}
          </span>
        </div>
        <span style={textBase({ fontSize: 10 })}>{endtimeText}</span>
      </div>
    </div>
  );

  const specialStudios = [
    "N/A",
    "Critical Events",
    "Post Production",
    "Staff",
    "Adam",
  ];
  if (specialStudios.includes(event.event.extendedProps.studio)) {
    return wrapEvent(returnExtendedPropsTitle);
  }
  if (!event.event.extendedProps.jobTypeFull) return "";

  let smallEvent = false;
  if (event.event.end) {
    if (!event.event.extendedProps.staffAllDay) {
      const start = new Date(event.event.bookedStart).getTime();
      const end = new Date(event.event.bookedFinish).getTime();
      if (end - start < 6000000) {
        smallEvent = true;
      }
    }
    smallEvent = false;
  }
  const clientNameContainerStyle = flexColumn({
    flexDirection: event.view.type === "dayGridMonth" ? "row" : "column",
    fontSize: "0.95em",
  });

  const html = (
    <div style={containerStyle}>
      {!event.event.extendedProps.staffAllDay ? (
        <>
          <span style={notStaffAllDayStyle}>
            <CircleIcon
              fontSize="small"
              sx={{ width: 15 }}
              style={circleStyle(event.event.extendedProps.confirmationStatus)}
            />
            {event.event.extendedProps.express ? (
              <CircleIcon
                fontSize="small"
                sx={{ width: 15 }}
                style={{ fill: "#d9418d", marginRight: 4, stroke: "#303030" }}
              />
            ) : null}
            {timeText}
          </span>
          {parseStudioSmall(event.event.extendedProps.studio) ? (
            <span
              style={{
                fontWeight: "bold",
                fontSize: "0.7rem",
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              {parseStudioSmall(event.event.extendedProps.studio)}
            </span>
          ) : null}
          {event.view.type === "dayGridMonth" ? <span>&nbsp;</span> : null}
          <div style={clientNameContainerStyle}>
            <span
              style={{
                marginLeft: event.view.type === "dayGridMonth" ? 5 : 0,
                fontFamily:
                  '"HelveticaNeue-Bold", "Helvetica Neue Bold", "Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif',
              }}
            >
              {event.event.extendedProps.isAdditionalDate ? "🔗 " : ""}
              {event.event.extendedProps.client}{" "}
              {event.event.extendedProps.multipleDays
                ? ` - ${event.event.extendedProps.multipleDays}`
                : ""}{" "}
              {event.event.extendedProps.comments
                ? ` - ${event.event.extendedProps.comments}`
                : ""}{" "}
              {event.event.extendedProps.imagesScheduledToBeShot
                ? ` - ${event.event.extendedProps.imagesScheduledToBeShot}`
                : ""}
            </span>
            <span
              style={{
                marginLeft: event.view.type === "dayGridMonth" ? 5 : 0,
                fontSize: 11,
              }}
            >
              {event.event.extendedProps.jobNumber}{" "}
              {event.event.extendedProps.jobTypeFull ? "-" : ""}{" "}
              {smallEvent
                ? event.event.extendedProps.jobTypeFull.replace(/ \(.*\)/, "")
                : event.event.extendedProps.jobTypeFull}
            </span>
            <span style={{ marginTop: 10 }}>
              {event.event.extendedProps.eventNotes}
            </span>
          </div>
          <div
            style={{
              position: "absolute",
              bottom: 0,
              fontWeight: "bold",
              fontSize: "0.7rem",
            }}
          >
            {endtimeText}
          </div>
        </>
      ) : (
        <div style={staffAllDayStyle}>
          {buildPrefixLabel(event.event.extendedProps)}
          {parseShooter(event.event.extendedProps)}
          {findStaff(
            event.event.extendedProps.photographer,
            staffData,
            event,
            events,
            showDoubles
          )}
          {parseAdditionalStaff(
            "photographer",
            event,
            staffData,
            showDoubles,
            events
          )}{" "}
          {parseOperator(event.event.extendedProps)}
          {findStaff(
            event.event.extendedProps.operator,
            staffData,
            event,
            events,
            showDoubles
          )}
          {parseAdditionalStaff(
            "operator",
            event,
            staffData,
            showDoubles,
            events
          )}{" "}
          (S){" "}
          {findStaff(
            event.event.extendedProps.stylist,
            staffData,
            event,
            events,
            showDoubles
          )}
          {parseAdditionalStaff(
            "stylist",
            event,
            staffData,
            showDoubles,
            events
          )}
        </div>
      )}
    </div>
  );

  return wrapEvent(html);
};

export const eventContentWithListView = (
  event,
  staffData,
  events,
  showDoubles,
  isListView,
  calendarColours,
  theme
) => {

  const isCurrentEventListView =
    isListView || LIST_VIEWS.includes(event.view.type);

  if (isCurrentEventListView) {
    const studio = event.event.extendedProps.studio;
    const backgroundColor = studio
      ? theme === "dark"
        ? calendarColours[studio]?.darkBackground || "#000000"
        : calendarColours[studio]?.background || "#000000"
      : "#cccccc";

    const textColor = studio
      ? theme === "dark"
        ? calendarColours[studio]?.darkText || "#ffffff"
        : calendarColours[studio]?.text || "#000000"
      : "#000000";

    let jobTitle = event.event.title || "";

    if (
      studio === "Staff" ||
      studio === "N/A" ||
      studio === "Critical Events" ||
      studio === "Adam"
    ) {
      const client = event.event.extendedProps.client;
      const jobTypeFull = event.event.extendedProps.jobTypeFull;
      if (client || jobTypeFull) {
        jobTitle = `${client || ""} ${jobTypeFull ? "-" : ""} ${
          jobTypeFull || ""
        }`.trim();
      }
    }

    const photographerElement = findStaff(
      event.event.extendedProps.photographer,
      staffData,
      event,
      events,
      showDoubles
    );
    const operatorElement = findStaff(
      event.event.extendedProps.operator,
      staffData,
      event,
      events,
      showDoubles
    );
    const stylistElement = findStaff(
      event.event.extendedProps.stylist,
      staffData,
      event,
      events,
      showDoubles
    );

    const additionalPhotographersElement = parseAdditionalStaff(
      "photographer",
      event,
      staffData,
      showDoubles,
      events
    );
    const additionalOperatorsElement = parseAdditionalStaff(
      "operator",
      event,
      staffData,
      showDoubles,
      events
    );
    const additionalStylistsElement = parseAdditionalStaff(
      "stylist",
      event,
      staffData,
      showDoubles,
      events
    );

    return (
      <div
        className="list-event-item"
        style={listContainer(backgroundColor, textColor)}
      >
        <div style={listTitle}>
          <span>{jobTitle}</span>
          {studio && <span style={listStudioPill(theme)}>{studio}</span>}
        </div>
        <div style={listMetaRow}>
          {photographerElement && (
            <span style={listStaffTag}>
              <span>(P)</span>
              {photographerElement}
            </span>
          )}
          {additionalPhotographersElement && (
            <span style={listStaffTag}>
              <span>(P)</span>
              {additionalPhotographersElement}
            </span>
          )}
          {operatorElement && (
            <span style={listStaffTag}>
              <span>(O)</span>
              {operatorElement}
            </span>
          )}
          {additionalOperatorsElement && (
            <span style={listStaffTag}>
              <span>(O)</span>
              {additionalOperatorsElement}
            </span>
          )}
          {stylistElement && (
            <span style={listStaffTag}>
              <span>(S)</span>
              {stylistElement}
            </span>
          )}
          {additionalStylistsElement && (
            <span style={listStaffTag}>
              <span>(S)</span>
              {additionalStylistsElement}
            </span>
          )}
        </div>
        {event.event.extendedProps.eventNotes ? (
          <div style={listNotes({ fontWeight: "bold" })}>
            <span>{event.event.extendedProps.eventNotes}</span>
          </div>
        ) : null}
        {event.event.extendedProps.comments ||
        event.event.extendedProps.imagesScheduledToBeShot ? (
          <div style={listNotes()}>
            <span>{event.event.extendedProps.comments}</span>
            {event.event.extendedProps.comments &&
              event.event.extendedProps.imagesScheduledToBeShot && (
                <span> - </span>
              )}
            <span>{event.event.extendedProps.imagesScheduledToBeShot}</span>
          </div>
        ) : null}
      </div>
    );
  }

  return eventContent(event, staffData, events, showDoubles);
};
